import React, { FC } from 'react';
import { Pane, Flex, Text, Middot, Button, Box, Tooltip } from '@fullstory/components';
import { timestamp } from '@fullstory/proto/google/protobuf';
import { FormattedMessage } from 'react-intl';
import { intl } from '@fullstory/intl';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import { VisibilityLabel } from '../VisibilityLabel';

export interface Metadata {
  createdBy: string;
  createdAt: timestamp | null;
  updatedBy: string;
  updatedAt: timestamp | null;
  rightAdornment?: React.ReactNode;
  owner?: string;
  visibility?: Visibility;
}

interface MetadataDisplayProps {
  metadata: Metadata;
  onModifyOwner?: () => void;
  disableBottomBorder?: boolean;
  disableTopBorder?: boolean;
  canUpdate?: boolean;
  disableBackground?: boolean;
}

export const MetadataDisplay: FC<MetadataDisplayProps> = ({
  metadata,
  onModifyOwner,
  disableBottomBorder,
  disableTopBorder,
  canUpdate = true,
  disableBackground,
}) => {
  const createdBy = metadata.createdBy === 'system' ? 'Fullstory' : metadata.createdBy;
  const updatedBy = metadata.updatedBy === 'system' ? 'FullStory' : metadata.updatedBy;
  return (
    <Pane
      data-component="MetadataDisplay"
      backgroundColor={disableBackground ? 'default' : 'secondary'}
      padding={
        disableTopBorder && disableBottomBorder && disableBackground ? undefined : [16, 24, 16, 24]
      }
      borderBottom={disableBottomBorder ? undefined : 'standard'}
      borderTop={disableTopBorder ? undefined : 'standard'}
    >
      <Flex justifyContent="space-between" gap={8} style={{ maxWidth: '100%' }}>
        <Flex column alignItems="flex-start" style={{ overflow: 'hidden', position: 'relative' }}>
          <Flex justifyContent="flex-start" style={{ maxWidth: '100%' }} gap={4}>
            {metadata.owner ? (
              <Box display="flex" width={1}>
                <Text appearance="bodySmallBold">
                  <FormattedMessage
                    defaultMessage="Owner"
                    id="ET+gHK"
                    description="Indicates the owner of an object"
                  />
                </Text>
                <Middot />
                <Text appearance="bodySmallBold" noWrap>
                  {metadata.owner === 'system'
                    ? intl.formatMessage({ defaultMessage: 'Fullstory', id: 'aM7vPS' })
                    : metadata.owner}
                </Text>
                {onModifyOwner && canUpdate && (
                  <>
                    <Middot />
                    <Box flexGrow={1}>
                      <Button onClick={onModifyOwner} variant="minimal" size="x-small">
                        <FormattedMessage
                          defaultMessage="Modify"
                          id="hDBi91"
                          description="Button used to modify owner of an object"
                        />
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            ) : (
              <Box display="flex" width={1}>
                <Text appearance="bodySmallBold">
                  <FormattedMessage
                    defaultMessage="Created"
                    id="qsZzgy"
                    description="Indicates the user that created an object"
                  />
                </Text>
                <Middot />
                <Tooltip
                  tooltipContent={createdBy}
                  showDelay={300}
                  childrenOverflowHidden
                  testId="createdByTooltip"
                >
                  <Text appearance="bodySmallBold" noWrap>
                    {createdBy}
                  </Text>
                </Tooltip>
                {metadata.createdAt && (
                  <Box flexGrow={1}>
                    <Text appearance="bodySmallBold">
                      &nbsp;
                      {`(${format(metadata.createdAt)})`}
                    </Text>
                  </Box>
                )}
              </Box>
            )}
            {metadata?.visibility === Visibility.PRIVATE && (
              <VisibilityLabel
                visibility={metadata.visibility}
                showLock
                tooltipContent={intl.formatMessage({
                  defaultMessage: 'This object is only visible to you.',
                  id: 'd6ciTa',
                })}
              />
            )}
          </Flex>
          {updatedBy && (
            <Flex style={{ maxWidth: '100%' }}>
              <Text appearance="bodySmall" color="secondary">
                <FormattedMessage
                  defaultMessage="Updated"
                  id="EqfKLa"
                  description="Indicates the user that updated an object"
                />
              </Text>
              <Middot />
              <Tooltip
                tooltipContent={updatedBy}
                showDelay={300}
                childrenOverflowHidden
                testId="updatedByTooltip"
              >
                <Text appearance="bodySmall" color="secondary" noWrap>
                  {updatedBy}
                </Text>
              </Tooltip>
              {metadata.updatedAt && (
                <Box flexGrow={1}>
                  <Text appearance="bodySmall" color="secondary">
                    &nbsp;
                    {`(${format(metadata.updatedAt)})`}
                  </Text>
                </Box>
              )}
            </Flex>
          )}
        </Flex>
        {metadata.rightAdornment}
      </Flex>
    </Pane>
  );
};

const format = (d: timestamp) => intl.formatDate(new Date(d as string), { dateStyle: 'short' });
